/**
 * DocumentModals.tsx — the dialogs of Admin > Documents, as legacy's Documents
 * section has them (html/lib/admin/admin-documents.php). Each has its own Save;
 * nothing else on the page changes when one is saved.
 *
 *   - DocumentModal: Title, Description, Signature, the File (the current one
 *     opens from its name; a new document is saved first, then reopened to
 *     upload) and its E-Signature Fields.
 *   - PacketModal: Name, Description, Signature, "Only send when the patient
 *     has", where its signature fields come from, and its own fields.
 *   - PacketItemModal: add a specific document, or a slot filled by the
 *     patient's procedure (1–300), to the end of a packet.
 *   - SignatureFieldsBlock: a document's or packet's fields, listed; "Manage
 *     fields" (or a click on one) opens the placement editor, which saves them
 *     on their own.
 * Legacy's section has no email / SMS template here; a stored link is kept.
 *
 * On a Locked page AdminFormModal makes each a viewer; these also drop the
 * upload and open the fields to read only.
 */
import { useEffect, useRef, useState } from 'react';
import { Eye, PenLine, Upload } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Input } from '@/components/ui/Input';
import { SearchableCombobox } from '@/components/ui/SearchableCombobox';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { Textarea } from '@/components/ui/Textarea';
import { cn } from '@/lib/utils';
import {
  DOCUMENT_DESCRIPTION_MAX,
  DOCUMENT_TITLE_MAX,
  DOCUMENT_UPLOAD_EXTENSIONS,
  DOCUMENT_UPLOAD_MAX_BYTES,
  KEYWORD_SOURCE_OPTIONS,
  PACKET_NAME_MAX,
  PACKET_REQUIRES_OPTIONS,
  PROCEDURE_SLOT_MAX,
  SIGNATURE_TYPE_OPTIONS,
  addPacketEntry,
  createDocumentPacket,
  createLibraryDocument,
  getEsignFields,
  getLibraryDocumentFileUrl,
  updateDocumentPacket,
  updateLibraryDocument,
  uploadLibraryDocumentFile,
  type DocumentPacket,
  type DocumentPayload,
  type EsignField,
  type FieldOwnerType,
  type LibraryDocument,
  type PacketEntry,
  type PacketPayload,
  type PacketRequirement,
} from '@/services/documentLibrary.service';
import { toast } from '@/store/toastStore';
import { AdminFormModal } from '../AdminFormModal';
import { useAdminPageLevel } from '../AdminPageLevelContext';
import { DialogField, fieldClasses, type FieldState } from '../DialogChrome';
import { useTrackedForm } from '../useTrackedForm';
import { FieldsSummary } from '../esign/FieldsSummary';
import { PlacementEditorDialog } from '../esign/PlacementEditorDialog';
import { compareDocumentTitles } from './documentsBoardState';

const UPLOAD_ACCEPT = DOCUMENT_UPLOAD_EXTENSIONS.map((ext) => `.${ext}`).join(',');
const UPLOAD_REFUSED = 'That file could not be uploaded. Check it is a Word, PDF, or RTF document under 20MB.';

/** A stored format that is not a signature layout (v5 once saved file extensions) reads as none. */
function signatureValue(format: string): string {
  return SIGNATURE_TYPE_OPTIONS.some((o) => o.value === format) ? format : '';
}

const fieldState = (missing: boolean): FieldState => (missing ? 'error' : 'none');

/** Only the fields that changed, for a PATCH. */
function changedOnly<T extends object>(form: T, changed: Set<keyof T>): Partial<T> {
  const out: Partial<T> = {};
  for (const key of changed) out[key] = form[key];
  return out;
}

// ─── E-signature fields ─────────────────────────────────────────────────────

export function SignatureFieldsBlock({
  ownerType,
  ownerId,
  ownerTitle,
  fileName,
}: {
  ownerType: FieldOwnerType;
  ownerId: number;
  ownerTitle: string;
  /** Documents: the file to position on (one uploaded and not saved yet counts). */
  fileName?: string;
}) {
  const locked = useAdminPageLevel() === 'locked';
  const [fields, setFields] = useState<EsignField[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editor, setEditor] = useState<{ fieldId?: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    void getEsignFields(ownerType, ownerId).then((res) => {
      if (cancelled) return;
      if (res.ok) setFields(res.data.keywords);
      else setLoadError(res.message);
    });
    return () => {
      cancelled = true;
    };
  }, [ownerType, ownerId]);

  return (
    <DialogField label="E-Signature Fields">
      <div className="space-y-2" data-testid="esign-fields-block">
        {loadError ? (
          <p className="text-sm text-red-600">{loadError}</p>
        ) : fields === null ? (
          <Spinner size="sm" />
        ) : (
          <FieldsSummary fields={fields} onSelect={(fieldId) => setEditor({ fieldId })} />
        )}
        <div>
          <Button
            variant="secondary"
            size="sm"
            leftIcon={locked ? <Eye className="h-4 w-4" /> : <PenLine className="h-4 w-4" />}
            disabled={fields === null}
            onClick={() => setEditor({})}
          >
            {locked ? 'View fields' : 'Manage fields'}
          </Button>
        </div>
      </div>
      {editor && (
        <PlacementEditorDialog
          open
          onOpenChange={(open) => !open && setEditor(null)}
          ownerType={ownerType}
          ownerId={ownerId}
          ownerTitle={ownerTitle}
          fileName={fileName || undefined}
          initialFieldId={editor.fieldId}
          readOnly={locked}
          onSaved={(saved) => setFields(saved)}
        />
      )}
    </DialogField>
  );
}

// ─── Document ───────────────────────────────────────────────────────────────

interface DocumentForm {
  title: string;
  description: string;
  format: string;
  fileName: string;
}

export function DocumentModal({
  document,
  category,
  onClose,
  onSaved,
}: {
  /** Null to add one to `category`. */
  document: LibraryDocument | null;
  category: { id: number; name: string };
  onClose: () => void;
  onSaved: (saved: LibraryDocument, isNew: boolean) => void;
}) {
  const isNew = document === null;
  const locked = useAdminPageLevel() === 'locked';
  const t = useTrackedForm<DocumentForm>(
    () => ({
      title: document?.title ?? '',
      description: document?.description ?? '',
      format: signatureValue(document?.format ?? ''),
      fileName: document?.fileName ?? '',
    }),
    { tracking: !isNew },
  );
  const { form } = t;
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const missingTitle = form.title.trim() === '';
  const errors = [...(missingTitle ? ['Title is required'] : []), ...(uploading ? ['Wait for the upload to finish'] : [])];

  async function handleFile(file: File | undefined) {
    if (fileRef.current) fileRef.current.value = '';
    if (!file) return;
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!(DOCUMENT_UPLOAD_EXTENSIONS as readonly string[]).includes(ext) || file.size > DOCUMENT_UPLOAD_MAX_BYTES) {
      toast.error(UPLOAD_REFUSED);
      return;
    }
    setUploading(true);
    const res = await uploadLibraryDocumentFile(file);
    setUploading(false);
    if (!res.ok) {
      toast.error(res.message);
      return;
    }
    t.patch({ fileName: res.data.fileName });
    toast.success('File uploaded. Save to keep it.');
  }

  async function openSavedFile() {
    if (!document) return;
    const res = await getLibraryDocumentFileUrl(document.documentID);
    if (res.ok) window.open(res.data.url, '_blank', 'noopener');
    else toast.error(res.message);
  }

  async function handleSave() {
    if (errors.length > 0 || saving) return;
    setSaving(true);
    const title = form.title.trim();
    const res = document
      ? await updateLibraryDocument(document.documentID, { ...(changedOnly(form, t.changed) as DocumentPayload), ...(t.changed.has('title') ? { title } : {}) })
      : await createLibraryDocument({ categoryID: category.id, title, description: form.description, format: form.format });
    setSaving(false);
    if (!res.ok) {
      toast.error(res.message);
      return;
    }
    toast.success('Document saved');
    onSaved(res.data, isNew);
  }

  const savedFile = document !== null && document.fileName !== '' && form.fileName === document.fileName;
  const pendingFile = form.fileName !== '' && !savedFile;

  return (
    <AdminFormModal
      title={isNew ? 'Add Document' : 'Edit Document'}
      description={`In ${category.name}.`}
      isNew={isNew}
      dirty={isNew ? !missingTitle : t.dirty}
      saving={saving}
      errors={errors}
      saveLabel="Save"
      onSave={() => void handleSave()}
      onRevertAll={t.revertAll}
      onClose={onClose}
      size="lg"
    >
      <div className="grid gap-4">
        <DialogField
          label="Title"
          htmlFor="document-title"
          required
          state={fieldState(missingTitle)}
          labelHint={missingTitle ? '— required' : undefined}
          badge={t.badge('title', 'Title')}
        >
          <Input
            id="document-title"
            value={form.title}
            maxLength={DOCUMENT_TITLE_MAX}
            onChange={(e) => t.patch({ title: e.target.value })}
            className={fieldClasses.input(fieldState(missingTitle))}
          />
        </DialogField>

        <DialogField label="Description" htmlFor="document-description" badge={t.badge('description', 'Description')}>
          <Textarea
            autoResize
            id="document-description"
            rows={1}
            value={form.description}
            maxLength={DOCUMENT_DESCRIPTION_MAX}
            onChange={(e) => t.patch({ description: e.target.value })}
          />
        </DialogField>

        <DialogField label="Signature" htmlFor="document-signature" badge={t.badge('format', 'Signature')}>
          <Select<string>
            id="document-signature"
            options={[...SIGNATURE_TYPE_OPTIONS]}
            value={form.format}
            onChange={(v) => t.patch({ format: v ?? '' })}
          />
        </DialogField>

        <DialogField label="File" badge={t.badge('fileName', 'File')}>
          <div className="space-y-2" data-testid="document-file">
            {savedFile ? (
              <p className="text-sm text-slate-700">
                Current file:{' '}
                <button type="button" onClick={() => void openSavedFile()} title="Download this file" className="font-medium text-blue-700 hover:underline">
                  {form.fileName}
                </button>
              </p>
            ) : pendingFile ? (
              <p className="text-sm text-slate-700">
                New file: <span className="font-medium">{form.fileName}</span>
              </p>
            ) : (
              <p className="text-sm text-slate-500">No file uploaded yet.</p>
            )}
            {isNew ? (
              !locked && <p className="text-sm text-slate-500">Save the document first, then reopen it to upload a file.</p>
            ) : (
              !locked && (
                <>
                  <div
                    role="button"
                    tabIndex={0}
                    aria-label={form.fileName ? 'Replace file' : 'Upload file'}
                    onClick={() => !uploading && fileRef.current?.click()}
                    onKeyDown={(e) => {
                      if ((e.key === 'Enter' || e.key === ' ') && !uploading) {
                        e.preventDefault();
                        fileRef.current?.click();
                      }
                    }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      void handleFile(e.dataTransfer.files?.[0]);
                    }}
                    className={cn(
                      'flex cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-slate-300 px-4 py-4 text-center text-sm text-slate-500 transition-colors hover:bg-slate-50',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
                    )}
                  >
                    {uploading ? <Spinner size="sm" /> : <Upload className="h-5 w-5 text-slate-400" aria-hidden />}
                    <span>{uploading ? 'Uploading…' : 'Click or drag a Word, PDF, RTF, ODT, Excel or DYMO label file here (20MB at most)'}</span>
                  </div>
                  <input
                    ref={fileRef}
                    type="file"
                    accept={UPLOAD_ACCEPT}
                    className="hidden"
                    aria-label="Document file"
                    onChange={(e) => void handleFile(e.target.files?.[0])}
                  />
                </>
              )
            )}
          </div>
        </DialogField>

        {document && (
          <SignatureFieldsBlock ownerType="document" ownerId={document.documentID} ownerTitle={document.title} fileName={form.fileName} />
        )}
      </div>
    </AdminFormModal>
  );
}

// ─── Packet ─────────────────────────────────────────────────────────────────

interface PacketForm {
  name: string;
  description: string;
  format: string;
  requires: PacketRequirement[];
  keywordSource: 0 | 1;
}

export function PacketModal({
  packet,
  onClose,
  onSaved,
}: {
  /** Null to add one. */
  packet: DocumentPacket | null;
  onClose: () => void;
  onSaved: (saved: DocumentPacket, isNew: boolean) => void;
}) {
  const isNew = packet === null;
  const t = useTrackedForm<PacketForm>(
    () => ({
      name: packet?.name ?? '',
      description: packet?.description ?? '',
      format: signatureValue(packet?.format ?? ''),
      requires: packet?.requires ?? [],
      keywordSource: packet?.keywordSource === 1 ? 1 : 0,
    }),
    { tracking: !isNew },
  );
  const { form } = t;
  const [saving, setSaving] = useState(false);
  const missingName = form.name.trim() === '';

  function toggleRequirement(value: PacketRequirement, on: boolean) {
    const next = on ? [...form.requires, value] : form.requires.filter((r) => r !== value);
    // In legacy's order, so an unchanged set never reads as changed.
    t.patch({ requires: PACKET_REQUIRES_OPTIONS.map((o) => o.value).filter((v) => next.includes(v)) });
  }

  async function handleSave() {
    if (missingName || saving) return;
    setSaving(true);
    const payload: PacketPayload = packet
      ? { ...changedOnly(form, t.changed), ...(t.changed.has('name') ? { name: form.name.trim() } : {}) }
      : { ...form, name: form.name.trim() };
    const res = packet ? await updateDocumentPacket(packet.documentPacketID, payload) : await createDocumentPacket(payload);
    setSaving(false);
    if (!res.ok) {
      toast.error(res.message);
      return;
    }
    toast.success('Packet saved');
    onSaved(res.data, isNew);
  }

  return (
    <AdminFormModal
      title={isNew ? 'Add Packet' : 'Edit Packet'}
      isNew={isNew}
      dirty={isNew ? !missingName : t.dirty}
      saving={saving}
      errors={missingName ? ['Name is required'] : []}
      saveLabel="Save"
      onSave={() => void handleSave()}
      onRevertAll={t.revertAll}
      onClose={onClose}
      size="lg"
    >
      <div className="grid gap-4">
        <DialogField
          label="Name"
          htmlFor="packet-name"
          required
          state={fieldState(missingName)}
          labelHint={missingName ? '— required' : undefined}
          badge={t.badge('name', 'Name')}
        >
          <Input
            id="packet-name"
            value={form.name}
            maxLength={PACKET_NAME_MAX}
            onChange={(e) => t.patch({ name: e.target.value })}
            className={fieldClasses.input(fieldState(missingName))}
          />
        </DialogField>

        <DialogField label="Description" htmlFor="packet-description" badge={t.badge('description', 'Description')}>
          <Textarea autoResize id="packet-description" rows={1} value={form.description} onChange={(e) => t.patch({ description: e.target.value })} />
        </DialogField>

        <DialogField label="Signature" htmlFor="packet-signature" badge={t.badge('format', 'Signature')}>
          <Select<string> id="packet-signature" options={[...SIGNATURE_TYPE_OPTIONS]} value={form.format} onChange={(v) => t.patch({ format: v ?? '' })} />
        </DialogField>

        <DialogField label="Only send when the patient has" badge={t.badge('requires', 'Only send when the patient has')}>
          <div className="flex flex-wrap gap-6">
            {PACKET_REQUIRES_OPTIONS.map((o) => (
              <Checkbox
                key={o.value}
                id={`packet-requires-${o.value}`}
                label={o.label}
                checked={form.requires.includes(o.value)}
                onChange={(e) => toggleRequirement(o.value, e.target.checked)}
              />
            ))}
          </div>
        </DialogField>

        <DialogField
          label="Signature fields come from"
          htmlFor="packet-keyword-source"
          badge={t.badge('keywordSource', 'Signature fields come from')}
          description="Choosing the documents inside it uses the fields placed on each one, in its own pages. A field placed at a fixed spot on a page is left out, because its page only means something while that document is on its own."
        >
          <Select<number>
            id="packet-keyword-source"
            options={KEYWORD_SOURCE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            value={form.keywordSource}
            onChange={(v) => t.patch({ keywordSource: v === 1 ? 1 : 0 })}
          />
        </DialogField>

        {packet && <SignatureFieldsBlock ownerType="packet" ownerId={packet.documentPacketID} ownerTitle={packet.name} />}
      </div>
    </AdminFormModal>
  );
}

// ─── Packet item ────────────────────────────────────────────────────────────

export function PacketItemModal({
  packet,
  documents,
  onClose,
  onAdded,
}: {
  packet: DocumentPacket;
  /** The library; live documents are offered. */
  documents: LibraryDocument[];
  onClose: () => void;
  onAdded: (entry: PacketEntry) => void;
}) {
  const [kind, setKind] = useState<'document' | 'slot'>('document');
  const [documentId, setDocumentId] = useState<number | null>(null);
  const [slot, setSlot] = useState('1');
  const [saving, setSaving] = useState(false);

  const options = documents
    .filter((d) => d.active)
    .sort(compareDocumentTitles)
    .map((d) => ({ value: d.documentID, label: d.title.trim() || `Document #${d.documentID}` }));
  const slotNumber = Number(slot);
  const slotValid = /^\d+$/.test(slot.trim()) && slotNumber >= 1 && slotNumber <= PROCEDURE_SLOT_MAX;
  const errors =
    kind === 'document'
      ? documentId === null
        ? ['Choose a document']
        : []
      : slotValid
        ? []
        : [`Choose a procedure slot from 1 to ${PROCEDURE_SLOT_MAX}`];

  async function handleSave() {
    if (errors.length > 0 || saving) return;
    setSaving(true);
    const res = await addPacketEntry(
      packet.documentPacketID,
      kind === 'document' ? { documentType: 0, documentID: documentId as number } : { documentType: 1, packetWcId: slotNumber },
    );
    setSaving(false);
    if (!res.ok) {
      toast.error(res.message);
      return;
    }
    toast.success('Added to the packet');
    onAdded(res.data);
  }

  return (
    <AdminFormModal
      title="Add to Packet"
      description={`To the end of ${packet.name}.`}
      isNew
      dirty={kind === 'slot' || documentId !== null}
      saving={saving}
      errors={errors}
      saveLabel="Save"
      onSave={() => void handleSave()}
      onRevertAll={() => undefined}
      onClose={onClose}
      size="md"
    >
      <div className="grid gap-4">
        <DialogField label="What to add" htmlFor="packet-item-kind">
          <Select<string>
            id="packet-item-kind"
            options={[
              { value: 'document', label: 'A specific document' },
              { value: 'slot', label: "A slot filled by the patient's procedure" },
            ]}
            value={kind}
            onChange={(v) => setKind(v === 'slot' ? 'slot' : 'document')}
          />
        </DialogField>
        {kind === 'document' ? (
          <DialogField label="Document" htmlFor="packet-item-document" required state={fieldState(documentId === null)}>
            <SearchableCombobox<number>
              id="packet-item-document"
              options={options}
              value={documentId}
              onChange={setDocumentId}
              placeholder="Select a document"
              emptyMessage="No documents to add."
            />
          </DialogField>
        ) : (
          <DialogField label="Procedure slot" htmlFor="packet-item-slot" required state={fieldState(!slotValid)}>
            <Input
              id="packet-item-slot"
              type="number"
              min={1}
              max={PROCEDURE_SLOT_MAX}
              step={1}
              value={slot}
              onChange={(e) => setSlot(e.target.value)}
              className={cn('w-32', fieldClasses.input(fieldState(!slotValid)))}
            />
          </DialogField>
        )}
      </div>
    </AdminFormModal>
  );
}
